import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiCheck, FiX, FiClock, FiRefreshCw, FiSearch, 
  FiCreditCard, FiInfo, FiPaperclip, FiFileText, 
  FiAlertTriangle, FiDollarSign, FiUser, FiTag, 
  FiMessageSquare, FiHash, FiCheckCircle 
} from 'react-icons/fi';
import { paymentIntentAPI, portalAPI } from '../../services/api';

const formatCurrency = (value) => `${Number(value || 0).toLocaleString('fr-FR')} XOF`;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '-');

const PaymentIntentListPage = () => {
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [validationForm, setValidationForm] = useState({
    status: 'validated',
    payment_method_id: '',
    payment_mode: 'total',
    amount_paid: '',
    reference: '',
    notes: '',
  });

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchIntents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter) params.status = filter;
      const { data } = await paymentIntentAPI.listAgencyIntents(params);
      
      const parsedData = (Array.isArray(data) ? data : []).map(intent => {
        let metadata = intent.metadata;
        if (typeof metadata === 'string') {
          try { metadata = JSON.parse(metadata); } catch (e) { metadata = {}; }
        }
        return { ...intent, metadata: metadata || {} };
      });
      
      setIntents(parsedData);
    } catch (err) {
      console.error('Error fetching payment intents:', err);
      setIntents([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const { data } = await portalAPI.getPaymentMethods();
      setPaymentMethods(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Cannot load payment methods:', e);
    }
  }, []);

  useEffect(() => { 
    fetchIntents(); 
    fetchPaymentMethods();
  }, [fetchIntents, fetchPaymentMethods]);

  const openValidateModal = (intent) => {
    let metadata = intent.metadata;
    if (typeof metadata === 'string') {
      try { metadata = JSON.parse(metadata); } catch (e) { metadata = {}; }
    }
    
    setSelectedIntent({ ...intent, metadata: metadata || {} });
    setValidationForm({
      status: 'validated',
      payment_method_id: intent.requested_method_id || '',
      payment_mode: intent.intent_type === 'advance' ? 'avance' : intent.intent_type === 'installment' ? 'tranche' : 'total',
      amount_paid: intent.amount || '',
      reference: '',
      notes: '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedIntent(null);
  };

  const handleValidateSubmit = async (e) => {
    e.preventDefault();
    if (!selectedIntent) return;
    setProcessingId(selectedIntent.id);
    try {
      await paymentIntentAPI.validate(selectedIntent.id, {
        status: validationForm.status,
        payment_method_id: validationForm.payment_method_id || null,
        payment_mode: validationForm.payment_mode,
        amount_paid: validationForm.amount_paid ? parseFloat(validationForm.amount_paid) : undefined,
        reference: validationForm.reference || undefined,
        notes: validationForm.notes || undefined,
      });
      closeModal();
      fetchIntents();
      showToast('Paiement validé avec succès ! Quittance et solde mis à jour.');
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur lors de la validation', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (intentId) => {
    setRejectTargetId(intentId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectTargetId(null);
  };

  const handleReject = async () => {
    if (!rejectTargetId) return;
    setProcessingId(rejectTargetId);
    try {
      await paymentIntentAPI.validate(rejectTargetId, {
        status: 'rejected',
        notes: rejectReason || 'Rejeté par l\'agence',
      });
      closeRejectModal();
      fetchIntents();
      showToast('Intention de paiement rejetée.', 'error');
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur lors du rejet', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const filteredIntents = searchTerm
    ? intents.filter((i) =>
        (i.user_first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.user_last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.reference || i.id || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : intents;

  const statusColors = {
    pending: 'warning',
    validated: 'info',
    succeeded: 'success',
    rejected: 'danger',
    cancelled: 'secondary',
  };

  const statusLabels = {
    pending: 'En attente',
    validated: 'Validé',
    succeeded: 'Approuvé',
    rejected: 'Rejeté',
    cancelled: 'Annulé',
  };

  const stats = {
    total: intents.length,
    pending: intents.filter(i => i.status === 'pending').length,
    validated: intents.filter(i => i.status === 'validated' || i.status === 'succeeded').length,
    rejected: intents.filter(i => i.status === 'rejected').length,
  };

  // Composant Preuve de paiement Reçu
  const ReceiptViewer = ({ metadata }) => {
    if (!metadata) return null;
    let meta = metadata;
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta); } catch (e) { return null; }
    }
    
    const receiptUrl = meta.receipt_image;
    if (!receiptUrl) return null;
    
    const isImage = receiptUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) || 
                    receiptUrl.includes('/uploads/images/') ||
                    receiptUrl.startsWith('data:image');
    const isPdf = receiptUrl.match(/\.pdf$/i) || receiptUrl.includes('/uploads/documents/');

    return (
      <div style={{ 
        marginTop: 10, 
        padding: 12, 
        background: '#f0f9ff', 
        borderRadius: 10, 
        border: '1px solid #bae6fd' 
      }}>
        <div style={{ 
          fontWeight: 600, 
          marginBottom: 8, 
          color: '#0369a1', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 6,
          fontSize: 12
        }}>
          <FiPaperclip size={14} /> 
          Reçu / Preuve de transfert
          {meta.manual_payment && (
            <span style={{ 
              background: '#fef3c7', 
              color: '#92400e', 
              fontSize: 10, 
              padding: '1px 6px', 
              borderRadius: 4,
              fontWeight: 600
            }}>
              déclaration manuelle
            </span>
          )}
        </div>
        
        {isImage ? (
          <div>
            <a href={receiptUrl} target="_blank" rel="noopener noreferrer">
              <img 
                src={receiptUrl} 
                alt="Reçu de paiement" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 220, 
                  borderRadius: 6, 
                  border: '1px solid #e2e8f0',
                  cursor: 'pointer',
                  display: 'block',
                  objectFit: 'contain'
                }}
                onError={(e) => { 
                  e.target.style.display = 'none'; 
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'block'; 
                }}
              />
              <div style={{ display: 'none', padding: 10, background: '#fff', borderRadius: 6 }}>
                <FiFileText size={18} color="#64748b" style={{ marginRight: 6 }} />
                <span style={{ color: '#2563eb', fontSize: 12 }}>📎 Cliquer pour ouvrir le reçu</span>
              </div>
            </a>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ 
              width: 36, 
              height: 36, 
              background: '#fef3c7', 
              borderRadius: 6, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <FiFileText size={18} color="#d97706" />
            </div>
            <a 
              href={receiptUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2563eb', fontSize: 12, fontWeight: 600 }}
            >
              📎 Visualiser le document {isPdf ? '(PDF)' : ''}
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* ── Page Header ── */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Intentions de paiement</h1>
          <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0', fontSize: 13 }}>
            Gérez et validez les déclarations de paiement Orange Money, Wave ou espèces initiées par les locataires.
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={fetchIntents} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw className={loading ? 'spin' : ''} /> Actualiser
        </button>
      </div>

      {/* ── Stats Grid Responsive ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--primary-color, #2563eb)' }}>{stats.total}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>Total déclarations</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{stats.pending}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>En attente de validation</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>{stats.validated}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>Validés / Encaissés</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#ef4444' }}>{stats.rejected}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>Rejetés</div>
        </div>
      </div>

      {/* ── Filter and Search Bar ── */}
      <div className="card" style={{ marginBottom: 16, borderRadius: 10 }}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div className="tabs" style={{ gap: 4, margin: 0 }}>
            {[
              { key: 'pending', label: '⏳ En attente' },
              { key: 'validated', label: '✅ Validés' },
              { key: 'rejected', label: '❌ Rejetés' },
              { key: '', label: 'Tout voir' },
            ].map((s) => (
              <button
                key={s.key}
                className={`tab ${filter === s.key ? 'active' : ''}`}
                onClick={() => setFilter(s.key)}
                style={{ fontSize: 12, padding: '6px 14px' }}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div style={{ flex: '1 1 240px', maxWidth: 340, position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input
              type="text"
              className="form-control"
              placeholder="Rechercher locataire, réf..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: 32, fontSize: 13 }}
            />
          </div>
        </div>
      </div>

      {/* ── Main Data Table with Container ── */}
      <div className="card" style={{ borderRadius: 10, overflow: 'hidden' }}>
        <div className="table-container">
          <table className="table" style={{ width: '100%', minWidth: 880, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg, #f8fafc)', borderBottom: '2px solid var(--border, #e2e8f0)' }}>
                <th style={thStyle}>Locataire</th>
                <th style={thStyle}>Référence</th>
                <th style={thStyle}>Montant</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Méthode</th>
                <th style={thStyle}>Reçu</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Statut</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 40 }}>
                    <div className="spinner" />
                  </td>
                </tr>
              ) : filteredIntents.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 40 }}>
                    <FiClock size={32} style={{ marginBottom: 8, color: 'var(--text-secondary)' }} />
                    <h3 style={{ fontSize: 15, margin: '6px 0 2px' }}>Aucune intention de paiement</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>
                      {filter === 'pending'
                        ? 'Aucune demande de paiement en attente de validation.'
                        : 'Aucun résultat trouvé pour ce filtre.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredIntents.map((intent) => {
                  let meta = intent.metadata;
                  if (typeof meta === 'string') {
                    try { meta = JSON.parse(meta); } catch (e) { meta = {}; }
                  }
                  const hasReceipt = !!(meta && meta.receipt_image);
                  
                  return (
                    <tr key={intent.id} style={{ borderBottom: '1px solid var(--border, #e2e8f0)' }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>
                          {intent.user_first_name} {intent.user_last_name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                          {intent.user_email || intent.tenant_phone}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <code style={{ fontSize: 11, color: '#2563eb' }}>
                          {intent.reference || intent.id.slice(0, 8)}
                        </code>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: '#0f172a' }}>
                        {formatCurrency(intent.amount)}
                      </td>
                      <td style={tdStyle}>
                        {intent.intent_type === 'advance' ? (
                          <span className="badge badge-info" style={{ fontSize: 11 }}>
                            Avance {meta?.total_months ? `(${meta.total_months} mois)` : ''}
                          </span>
                        ) : intent.intent_type === 'installment' ? (
                          <span className="badge badge-warning" style={{ fontSize: 11 }}>
                            Tranche
                          </span>
                        ) : intent.intent_type === 'single' ? (
                          <span className="badge badge-success" style={{ fontSize: 11 }}>
                            Complet
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>Standard</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {intent.payment_method_name ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                            <FiCreditCard size={12} color="#64748b" /> {intent.payment_method_name}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>Non spécifié</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {hasReceipt ? (
                          <span style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: 4,
                            background: '#dbeafe',
                            color: '#1d4ed8',
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: 10
                          }}>
                            <FiPaperclip size={11} /> Reçu joint
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12 }}>{formatDate(intent.created_at)}</td>
                      <td style={tdStyle}>
                        <span className={`badge badge-${statusColors[intent.status] || 'secondary'}`} style={{ fontSize: 11, fontWeight: 700 }}>
                          {statusLabels[intent.status] || intent.status}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {intent.status === 'pending' ? (
                          <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openValidateModal(intent)}
                              disabled={processingId === intent.id}
                              style={{ padding: '4px 10px', fontSize: 12, background: '#16a34a', borderColor: '#16a34a' }}
                            >
                              <FiCheck /> Valider
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => openRejectModal(intent.id)}
                              disabled={processingId === intent.id}
                              style={{ padding: '4px 10px', fontSize: 12, color: '#dc2626', borderColor: '#dc2626' }}
                            >
                              <FiX /> Rejeter
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500 }}>
                            {intent.status === 'validated' || intent.status === 'succeeded' ? '✓ Traité' : '✕ Fermé'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Toast Notification ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: 10,
          background: toast.type === 'error' ? '#ef4444' : '#16a34a',
          color: '#fff', padding: '12px 20px', borderRadius: 10,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          fontWeight: 600, fontSize: 13,
        }}>
          {toast.type === 'error' ? <FiAlertTriangle size={18} /> : <FiCheckCircle size={18} />}
          {toast.msg}
        </div>
      )}

      {/* ── Modal de Rejet Responsive ── */}
      {showRejectModal && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content card" style={{ maxWidth: 460, width: '100%', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ background: '#dc2626', padding: '16px 20px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FiAlertTriangle size={20} />
                <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>Rejeter l'intention de paiement</h3>
              </div>
              <button onClick={closeRejectModal} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 12px' }}>
                Précisez le motif du rejet (reçu non conforme, référence introuvable, etc.).
              </p>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={labelStyle}>Motif du rejet (optionnel)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Ex: Reçu non lisible, virement non constaté sur le compte Orange Money..."
                />
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border, #e2e8f0)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#f8fafc' }}>
              <button className="btn btn-secondary btn-sm" onClick={closeRejectModal}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={handleReject} disabled={processingId === rejectTargetId} style={{ background: '#dc2626', borderColor: '#dc2626' }}>
                {processingId === rejectTargetId ? 'Traitement...' : 'Confirmer le rejet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de Validation Responsive & Senior ── */}
      {showModal && selectedIntent && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content card" style={modalContentStyle}>
            {/* Header */}
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a' }}>
                  <FiCheckCircle color="#16a34a" size={20} />
                  Valider la Déclaration de Paiement #{selectedIntent.reference || selectedIntent.id.slice(0, 8)}
                </h2>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                  Locataire : <strong>{selectedIntent.user_first_name} {selectedIntent.user_last_name}</strong> · Montant : <strong style={{ color: '#16a34a' }}>{formatCurrency(selectedIntent.amount)}</strong>
                </p>
              </div>
              <button onClick={closeModal} style={closeBtnStyle}><FiX size={20} /></button>
            </div>

            {/* Corps du modal en grille responsive */}
            <form onSubmit={handleValidateSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                  
                  {/* Colonne GAUCHE : Saisie de Validation */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={sectionTitleStyle}>
                      <FiTag size={14} color="#2563eb" /> Confirmation du Règlement
                    </div>

                    {/* Méthode de paiement */}
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={labelStyle}>Canal de Réception Encaissé *</label>
                      <select
                        className="form-control"
                        value={validationForm.payment_method_id}
                        onChange={e => setValidationForm({...validationForm, payment_method_id: e.target.value})}
                      >
                        <option value="">Sélectionner la méthode</option>
                        {paymentMethods.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Mode de paiement */}
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={labelStyle}>Mode d'Imputation</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                        {[
                          { value: 'total', label: 'Total' },
                          { value: 'tranche', label: 'Tranche' },
                          { value: 'avance', label: 'Avance' },
                        ].map(mode => (
                          <button
                            key={mode.value}
                            type="button"
                            className={`btn btn-sm ${validationForm.payment_mode === mode.value ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => setValidationForm({...validationForm, payment_mode: mode.value})}
                            style={{ fontSize: 12, padding: '6px 4px' }}
                          >
                            {mode.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Montant payé */}
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={labelStyle}>Montant Réellement Encaissé (XOF) *</label>
                      <input
                        type="number" step="0.01"
                        className="form-control"
                        value={validationForm.amount_paid}
                        onChange={e => setValidationForm({...validationForm, amount_paid: e.target.value})}
                        placeholder={selectedIntent.amount}
                        required
                      />
                      <small style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 2, display: 'block' }}>
                        Montant déclaré par le locataire : {formatCurrency(selectedIntent.amount)}
                      </small>
                    </div>

                    {/* Référence */}
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={labelStyle}>Référence Reçu / Transaction</label>
                      <input
                        type="text" 
                        className="form-control"
                        value={validationForm.reference}
                        onChange={e => setValidationForm({...validationForm, reference: e.target.value})}
                        placeholder="Ex: OM-2409-98745, Espèces reçu N°..."
                      />
                    </div>

                    {/* Notes */}
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={labelStyle}>Observations & Notes Internes</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={validationForm.notes}
                        onChange={e => setValidationForm({...validationForm, notes: e.target.value})}
                        placeholder="Remarques pour la comptabilité..."
                      />
                    </div>
                  </div>

                  {/* Colonne DROITE : Déclaration Locataire & Reçu */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={sectionTitleStyle}>
                      <FiPaperclip size={14} color="#7c3aed" /> Justificatif & Déclaration Locataire
                    </div>

                    {/* Déclaration locataire */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Locataire :</span>
                        <strong>{selectedIntent.user_first_name} {selectedIntent.user_last_name}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Montant réclamé :</span>
                        <strong style={{ color: '#16a34a' }}>{formatCurrency(selectedIntent.amount)}</strong>
                      </div>
                      {selectedIntent.metadata?.reference && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Réf. déclarée :</span>
                          <code>{selectedIntent.metadata.reference}</code>
                        </div>
                      )}
                      {selectedIntent.metadata?.notes && (
                        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #e2e8f0', color: '#475569' }}>
                          <em>« {selectedIntent.metadata.notes} »</em>
                        </div>
                      )}
                    </div>

                    {/* Reçu image ou PDF */}
                    <ReceiptViewer metadata={selectedIntent.metadata} />
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div style={modalFooterStyle}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={closeModal}>
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={processingId === selectedIntent.id}
                  style={{ background: '#16a34a', borderColor: '#16a34a', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <FiCheckCircle size={15} />
                  {processingId === selectedIntent.id ? 'Validation en cours...' : 'Confirmer l\'encaissement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: 'var(--text-secondary, #64748b)', textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};
const tdStyle = { padding: '12px 14px', fontSize: 13, verticalAlign: 'middle' };
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4, display: 'block' };
const sectionTitleStyle = {
  fontSize: 12, fontWeight: 700, color: '#1e293b',
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
  display: 'flex', alignItems: 'center', gap: 6,
};
const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 2000, padding: '16px',
};
const modalContentStyle = {
  maxWidth: 860, width: '100%', maxHeight: 'calc(100vh - 32px)',
  borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column',
  background: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  border: '1px solid var(--border, #e2e8f0)',
};
const modalHeaderStyle = {
  padding: '14px 20px', borderBottom: '1px solid var(--border, #e2e8f0)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#f8fafc', flexShrink: 0,
};
const modalFooterStyle = {
  padding: '12px 20px', borderTop: '1px solid var(--border, #e2e8f0)',
  display: 'flex', justifyContent: 'flex-end', gap: 10,
  background: '#f8fafc', flexShrink: 0,
};
const closeBtnStyle = {
  border: 'none', background: 'transparent', cursor: 'pointer',
  color: 'var(--text-secondary, #64748b)', padding: 4, borderRadius: 6,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default PaymentIntentListPage;
